import { z } from 'zod';

/**
 * Bus event emitted when a member issues an API key. Ids + the granted
 * permission set only — no secret, no PII — per ADR 0015 §PII. The audit
 * mapper treats `memberId` as the actor (a key is bound to, and issued
 * by, that member).
 *
 * Both ids are null for an admin API key (IMM-556.1), which binds to an operator
 * rather than to a member inside a workspace, and whose `grantedPermissions` is
 * always empty. Nullable rather than a second event type: every consumer of this
 * event cares about the issuance, and a parallel `admin-key-issued` would fork
 * the audit trail over a binding difference.
 */
export const ApiKeyIssuedPayloadSchema = z.object({
  keyId: z.uuid(),
  workspaceId: z.uuid().nullable(),
  memberId: z.uuid().nullable(),
  grantedPermissions: z.array(z.string()),
  occurredAt: z.iso.datetime(),
});

export type ApiKeyIssuedPayload = z.infer<typeof ApiKeyIssuedPayloadSchema>;

/**
 * Bus event emitted when a member revokes an API key. `memberId` is the
 * revoking member (the audit actor), distinct from the key's bound issuer.
 * Emitted only on a real active→revoked transition — re-revoking a
 * revoked key is a no-op and emits nothing.
 *
 * Both ids are null when an operator revokes an admin API key (IMM-556.1): there is
 * no workspace it belonged to and no member who acted.
 */
export const ApiKeyRevokedPayloadSchema = z.object({
  keyId: z.uuid(),
  workspaceId: z.uuid().nullable(),
  memberId: z.uuid().nullable(),
  occurredAt: z.iso.datetime(),
});

export type ApiKeyRevokedPayload = z.infer<typeof ApiKeyRevokedPayloadSchema>;

/**
 * Bus event emitted when a key's granted permissions and rate limit are
 * rewritten in place — by an operator (IMM-493.1) or by a workspace owner/admin
 * (IMM-1093.1). Emitted only on a real change — an edit whose values already
 * match writes nothing and emits nothing, so no audit row ever claims an edit
 * nobody made.
 *
 * Two actor variants, exactly one present: `updatedByAdminId` is the operator
 * who acted (audited as `actorType: 'ADMIN'`), `updatedByMemberId` the workspace
 * member who acted (audited as `actorType: 'MEMBER'`). `memberId` is always the
 * key's bound member — the subject the key authenticates as, which no edit
 * changes.
 *
 * Carries BOTH the previous and the resulting values, so "what did this key
 * look like before the edit?" is answerable from a single row. A diff is
 * derivable from before/after; the reverse needs the whole history replayed.
 */
export const ApiKeyUpdatedPayloadSchema = z
  .object({
    keyId: z.uuid(),
    workspaceId: z.uuid(),
    memberId: z.uuid(),
    updatedByAdminId: z.uuid().optional(),
    updatedByMemberId: z.uuid().optional(),
    previousRateLimit: z.number().int(),
    rateLimit: z.number().int(),
    previousPermissions: z.array(z.string()),
    grantedPermissions: z.array(z.string()),
    occurredAt: z.iso.datetime(),
  })
  .refine((p) => (p.updatedByAdminId === undefined) !== (p.updatedByMemberId === undefined), {
    message: 'exactly one of updatedByAdminId / updatedByMemberId',
  });

export type ApiKeyUpdatedPayload = z.infer<typeof ApiKeyUpdatedPayloadSchema>;
